import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/esm/extends";
var _excluded = ["name", "value", "defaultValue", "placeholder", "step", "min", "max", "onChange", "disabled", "resetable", "prefix", "suffix", "jss", "error", "allowComma", "style", "className"];
import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import cn from 'classnames';
import createStyles from "../createStyles";
import useFocus from "../useFocus";
import { isEmpty } from "../utils";
import Input from "../Input";
import Row from "../Layout/Row";
import NumberPickerArrows from "./NumberPickerArrows";
import PickerResetWrapper from "../basePicker/PickerResetWrapper";
import InputStyles from "../Input/styles";
var affixesContainer = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: '46px',
  height: '44px'
};
export var useStyles = createStyles(function (theme) {
  return {
    root: {
      height: '46px',
      borderRadius: '2px',
      border: "solid 1px ".concat(theme.palette.border),
      color: "".concat(theme.palette.text.input, " !important"),
      transition: theme.transition.create(),
      '&:hover': InputStyles.hover(theme)
    },
    focused: InputStyles.focused(theme),
    disabled: InputStyles.disabled(theme),
    error: InputStyles.error(theme),
    input: {
      borderStyle: 'none',
      '&:hover': {
        borderStyle: 'none'
      },
      height: '100%'
    },
    inputWithSuffix: {
      paddingRight: 0
    },
    inputWithPrefix: {
      paddingLeft: 0
    },
    prefixContainer: _extends({}, affixesContainer, {
      borderRight: "solid 1px ".concat(theme.palette.border),
      marginRight: theme.spacer(3)
    }),
    suffixContainer: _extends({}, affixesContainer, {
      borderLeft: "solid 1px ".concat(theme.palette.border),
      marginLeft: theme.spacer(3)
    })
  };
}, {
  name: 'NumberPicker'
});
var inputJssOverride = {
  focused: {
    borderStyle: 'none',
    boxShadow: 'none'
  },
  disabled: {
    cursor: 'not-allowed',
    '&:hover': {
      borderStyle: 'none'
    }
  }
};
var NumberPicker = /*#__PURE__*/React.forwardRef(function NumberPicker(_ref, ref) {
  var name = _ref.name,
      value = _ref.value,
      defaultValue = _ref.defaultValue,
      placeholder = _ref.placeholder,
      step = _ref.step,
      min = _ref.min,
      max = _ref.max,
      onChange = _ref.onChange,
      disabled = _ref.disabled,
      resetable = _ref.resetable,
      prefix = _ref.prefix,
      suffix = _ref.suffix,
      jss = _ref.jss,
      error = _ref.error,
      allowComma = _ref.allowComma,
      style = _ref.style,
      className = _ref.className,
      otherInputProps = _objectWithoutProperties(_ref, _excluded);

  var classes = useStyles({
    jss: jss
  });

  var _useFocus = useFocus(),
      _useFocus2 = _slicedToArray(_useFocus, 2),
      focused = _useFocus2[0],
      focusRef = _useFocus2[1];

  var manipulateEvent = function manipulateEvent(event, newValue) {
    event.persist();
    Object.defineProperty(event, 'target', {
      value: {
        value: "".concat(newValue),
        name: name
      }
    });
    return event;
  };

  var isValidValue = function isValidValue(v) {
    var numberRegex = RegExp('(^-?([1-9]\\d*|0)?(?:\\.(\\d+)?)?$)');
    return numberRegex.test(v);
  };

  var handleInputChange = function handleInputChange(e) {
    if (allowComma) {
      Object.defineProperty(e, 'target', {
        value: _extends({}, e.target, {
          value: e.target.value.replace(',', '.'),
          name: name
        })
      });
    }

    var newValue = e.target.value;

    if (isValidValue(newValue)) {
      onChange(e);
    }
  };

  var formatFloat = function formatFloat(n) {
    return parseFloat(n.toFixed(4));
  };

  var getIncValue = function getIncValue() {
    var numericValue = Number(value);
    var noValue = isEmpty(value) || Number.isNaN(numericValue);

    if (noValue) {
      if (!isEmpty(defaultValue)) {
        return Number(defaultValue) + step;
      }

      if (!isNil(min)) {
        return min;
      }
    } else {
      if (!isNil(min) && numericValue < min) {
        return min;
      }

      return formatFloat(Math.round(numericValue / step) * step + step);
    }

    return step;
  };

  var getDecValue = function getDecValue() {
    var numericValue = Number(value);
    var noValue = isEmpty(value) || Number.isNaN(numericValue);

    if (noValue) {
      if (!isEmpty(defaultValue)) {
        return Number(defaultValue) - step;
      }

      if (!isNil(max)) {
        return max;
      }
    } else {
      if (!isNil(max) && numericValue > max) {
        return max;
      }

      return formatFloat(Math.round(numericValue / step) * step - step);
    }

    return step;
  };

  var disableInc = disabled || getIncValue() > max;
  var disableDec = disabled || getDecValue() < min;

  var handleInc = function handleInc(event) {
    return onChange(manipulateEvent(event, getIncValue()));
  };

  var handleDec = function handleDec(event) {
    return onChange(manipulateEvent(event, getDecValue()));
  };

  var handleReset = function handleReset(event) {
    onChange(manipulateEvent(event, defaultValue));
  };

  return /*#__PURE__*/React.createElement(Row, {
    fullWidth: true,
    wrap: false,
    align: "center",
    spacing: 0,
    ref: ref,
    className: cn(classes.root, _defineProperty({}, classes.focused, focused), _defineProperty({}, classes.disabled, disabled), _defineProperty({}, classes.error, error), className),
    style: style
  }, /*#__PURE__*/React.createElement(PickerResetWrapper, {
    resetable: resetable,
    onReset: handleReset,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(Input, _extends({
    name: name,
    ref: focusRef,
    prefix: prefix && /*#__PURE__*/React.createElement("div", {
      className: classes.prefixContainer
    }, prefix),
    suffix: suffix && /*#__PURE__*/React.createElement("div", {
      className: classes.suffixContainer
    }, suffix),
    className: cn(classes.input, _defineProperty({}, classes.inputWithPrefix, prefix), _defineProperty({}, classes.inputWithSuffix, suffix)),
    placeholder: placeholder,
    disabled: disabled,
    onChange: handleInputChange,
    value: value,
    jss: inputJssOverride
  }, otherInputProps)), /*#__PURE__*/React.createElement(NumberPickerArrows, {
    disableDec: disableDec,
    disableInc: disableInc,
    onInc: handleInc,
    onDec: handleDec
  })));
});
NumberPicker.defaultProps = {
  name: undefined,
  value: undefined,
  defaultValue: '',
  placeholder: undefined,
  step: 1,
  min: undefined,
  max: undefined,
  onChange: noop,
  disabled: false,
  resetable: false,
  prefix: undefined,
  suffix: undefined,
  error: false,
  allowComma: false,
  jss: undefined,
  style: undefined,
  className: undefined
};
NumberPicker.propTypes = {
  /** Number picker name to be set */
  name: PropTypes.string,

  /** value to be set */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** default value to be set - will be used when for reset, and inc/dec while value is not set */
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([''])]),

  /** Input placeholder text */
  placeholder: PropTypes.string,

  /** step that will be applied when incrementing and decrementing */
  step: PropTypes.number,

  /** min value to be set */
  min: PropTypes.number,

  /** max value to be set */
  max: PropTypes.number,

  /** Function to be called on input value change */
  onChange: PropTypes.func,

  /** Disable state in which input interaction is not available */
  disabled: PropTypes.bool,

  /** Define if the value can be reset */
  resetable: PropTypes.bool,

  /** Input class */
  className: PropTypes.string,

  /** String Or React Element to be shown before the input */
  prefix: PropTypes.node,

  /** String Or React Element to be shown after the input */
  suffix: PropTypes.node,

  /** Indicator for form error state  */
  error: PropTypes.bool,

  /** Indicator for comma as decimal separator */
  allowComma: PropTypes.bool,

  /** jss override object to customize the jss classes */
  jss: PropTypes.oneOfType([PropTypes.shape(), PropTypes.func]),

  /** Additional styles */
  style: PropTypes.shape()
};
export default NumberPicker;